/**
 * Vera capability registry: the single source of truth for WHERE roles are
 * applied. Every named gate (UI or BFF) checks a capability from this list,
 * and the Admin page renders "what this role unlocks" from the same objects,
 * so the listing cannot drift from behavior.
 *
 * Rules:
 *  - This file must stay dependency-free apart from './roles'. It is copied
 *    into the BFF as a generated module (npm run authz:sync); anything else
 *    it imports would break that.
 *  - Generic module access and Admin/Manager tiers are NOT capabilities;
 *    they stay on the prefix helpers in roles.ts (hasModuleAccess,
 *    isModuleAdmin, isModuleManager). Capabilities are the named,
 *    special-purpose gates.
 *  - Adding a gate anywhere in Vera means adding (or reusing) an entry here.
 *    The roles:audit script flags role literals that bypass this registry.
 */

import {
  ADMIN_FEE_APPROVER_ROLE,
  ADMIN_FEE_EDITOR_ROLE,
  BROADCAST_ROLE,
  DEMO_PAGES_ROLE,
  FINANCE_COMMISSIONS_ROLE,
  IMPFORM_MANAGER_ROLE,
  OPS_DELETE_ROLE,
  hasGlobalAdmin,
  type VeraRoleGroup,
} from './roles';

export interface VeraCapability {
  /** Stable id referenced by gates ("ops.delete-plan-year"). */
  id: string;
  /** Human phrasing shown on the Admin page ("Delete a Plan Year"). */
  label: string;
  /** Where it is enforced: UI surface and/or BFF route. Keep it literal. */
  surface: string;
  /** Display group on the Admin page. */
  group: VeraRoleGroup;
  /** Roles that satisfy the capability. Global Admin qualifies too, unless excludesGlobalAdmin. */
  roles?: readonly string[];
  /** Any role with this prefix also qualifies (e.g. "Ops." = every Ops tier). */
  anyRoleWithPrefix?: string;
  /**
   * Global Admin does NOT automatically qualify: only the listed roles do.
   * The rare marker for content the admin rank should not read (the VELT
   * meeting layer, whose agenda notes can be sensitive about people). An
   * Admin who also holds a listed role qualifies through that role like
   * anyone else.
   */
  excludesGlobalAdmin?: boolean;
}

export const CAPABILITIES: readonly VeraCapability[] = [
  // ── Platform ──
  {
    id: 'platform.manage-roles',
    label: 'Grant and revoke Vera roles',
    surface: '/admin Roles page (by user / by role / bulk paste) and Access Health',
    group: 'Platform',
    roles: [],
  },
  {
    id: 'platform.admin-sessions',
    label: 'View and revoke active sessions',
    surface: 'BFF /api/admin/sessions',
    group: 'Platform',
    roles: [],
  },
  {
    id: 'platform.demo-pages',
    label: 'Publish and manage public demo pages',
    surface: 'Admin > Demo Pages tab; BFF /api/demo-pages admin routes',
    group: 'Platform',
    roles: [DEMO_PAGES_ROLE],
  },
  {
    id: 'platform.broadcast',
    label: 'Broadcast the header announcement banner',
    surface: 'Admin > Broadcasts tab; BFF /api/shell/broadcasts write routes',
    group: 'Platform',
    roles: [BROADCAST_ROLE],
  },

  // ── Operations ──
  {
    id: 'ops.delete-plan-year',
    label: 'Delete a Plan Year',
    surface: 'Group Detail, plan-year wrench menu; BFF destructive endpoint (Phase 2)',
    group: 'Operations',
    roles: ['Ops.Admin', OPS_DELETE_ROLE],
  },
  {
    id: 'ops.delete-all-solutions',
    label: 'Delete all solutions on a Plan Year',
    surface: 'Group Detail, plan-year wrench menu; BFF destructive endpoint (Phase 2)',
    group: 'Operations',
    roles: ['Ops.Admin', OPS_DELETE_ROLE],
  },
  {
    id: 'ops.nuke-gi',
    label: 'Nuke GI data (plan-year or group scope)',
    surface: 'Group Info tab, admin wrench menu; BFF destructive endpoint (Phase 2)',
    group: 'Operations',
    roles: ['Ops.Admin', OPS_DELETE_ROLE],
  },
  {
    id: 'ops.delete-group',
    label: 'Delete an entire group',
    surface: 'Group Detail header trash button; BFF destructive endpoint (Phase 2)',
    group: 'Operations',
    roles: ['Ops.Admin'],
  },
  {
    // Same grant as ops.delete-group on purpose (see canTermGroup below), but
    // its own id: the gate error and the Admin listing should name the act,
    // and the write lands on the CRM ACCOUNT, not a Vera row.
    id: 'ops.term-group',
    label: 'Set or clear a group term date (CRM account)',
    surface: 'Group Detail Account tab, Term Date row; BFF /api/ops/groups/:id/term',
    group: 'Operations',
    roles: ['Ops.Admin'],
  },
  {
    id: 'ops.crm-matches',
    label: 'Repair CRM account links (bulk re-parent)',
    surface: '/ops/crm-matches page and left-nav entry',
    group: 'Operations',
    roles: ['Ops.DataAdmin'],
  },
  {
    id: 'ops.provision-sites',
    label: 'Provision group SharePoint document sites',
    surface: 'Group Documents tab provisioning; BFF /api/groupdocs site routes',
    group: 'Operations',
    roles: ['Ops.Admin', 'Ops.SiteProvisioner'],
  },
  {
    id: 'ops.manage-site-automation',
    label: 'Turn automatic SharePoint site creation on or off',
    surface:
      'Group Site Provisioning page auto-stamp toggle; BFF PUT /api/groupdocs/admin/auto-stamp. Deliberately narrower than ops.provision-sites: running a batch affects the groups you picked, this decides whether every new group gets a site without anyone asking.',
    group: 'Operations',
    roles: ['Ops.Admin'],
  },
  {
    id: 'ops.stage-guest-roster',
    label: 'Stage and verify the external-contact roster on group document sites',
    surface: 'Group Documents tab External access card (add/verify/flag rows); BFF roster staging routes. Staging never emails anyone.',
    group: 'Operations',
    anyRoleWithPrefix: 'Ops.',
  },
  {
    id: 'ops.manage-guest-invites',
    label: 'Send external guest invitations and revoke guest access',
    surface: 'Group Documents tab External access card (fire batch, send/resend, revoke); BFF roster fire/send/revoke routes. This is the capability that can email clients.',
    group: 'Operations',
    roles: ['Ops.Admin', 'Ops.GuestAccess'],
  },
  {
    id: 'ops.reassign-account-exec',
    label: 'Reassign a group’s Account Executive (Dynamics owner)',
    surface: 'Group Detail AE edit dialog; BFF account-owner route',
    group: 'Operations',
    anyRoleWithPrefix: 'Ops.',
  },
  {
    id: 'ops.set-broker-of-record',
    label: 'Set a group’s Broker of Record (Dynamics account lookup)',
    surface: 'Group Detail Overview BOR tile; BFF account-bor route',
    group: 'Operations',
    anyRoleWithPrefix: 'Ops.',
  },
  {
    id: 'ops.compose-email',
    label: 'Compose Outlook drafts from group pages',
    surface: 'BFF ops email routes',
    group: 'Operations',
    anyRoleWithPrefix: 'Ops.',
  },
  {
    id: 'ops.sound-foghorn',
    label: 'Sound the Foghorn and set its Teams cadence',
    surface: 'Operation Daylight Foghorn dialog; BFF /api/foghorn/sound',
    group: 'Operations',
    roles: ['Ops.Admin'],
  },
  {
    id: 'ops.manage-plan-docs',
    label: 'Register and manage plan design documents (SPD/BPD/addendum chains)',
    surface: 'Group Plan Design subtab, Plan Documents panel; BFF /api/plandocs routes',
    group: 'Operations',
    anyRoleWithPrefix: 'Ops.',
  },
  {
    id: 'ops.promote-plan-design',
    label: 'Promote extracted plan-design values into the registry',
    surface:
      'Plan-design extraction review (phase 2, docs/design/plan-design-documents.md); BFF /api/plandocs promote routes. Registering documents never writes the registry; this is the guarded write.',
    group: 'Operations',
    roles: ['Ops.Admin'],
  },

  // ── Catalog ──
  {
    id: 'catalog.manage-doc-structures',
    label: 'Author document structure modules',
    surface: 'Catalog Doc Structures page; BFF /api/groupdocs module routes',
    group: 'Catalog',
    roles: ['Catalog.Admin'],
  },
  {
    // D1 (7/23 review): these runs touch the ENTIRE book in one click, either
    // creating instances across every active group or deactivating them across
    // every active group. Until now any Catalog role could fire them.
    id: 'catalog.run-propagation',
    label: 'Run book-wide propagation and retired-data sweeps',
    surface: 'Catalog Propagation page (Pending run, Retired Data sweep, Bulk Update apply)',
    group: 'Catalog',
    roles: ['Catalog.Admin'],
  },

  {
    // Vendor intake (MED-TEMPLATE.md section 6, Sasha 2026-09-07): shred a new
    // vendor's sample document against the live dictionary, confirm the label
    // mappings as vendor aliases, batch-author the true gaps. Writes aliases
    // (Postgres) and drives Catalog field creation, so it stays with the people
    // who author the dictionary.
    id: 'catalog.vendor-intake',
    label: 'Run vendor intake against the plan-design dictionary',
    surface: 'Catalog > Plan Design > Vendor Intake; BFF /api/plandocs/intake routes',
    group: 'Catalog',
    anyRoleWithPrefix: 'Catalog.',
  },

  // ── Sales ──
  {
    id: 'sales.send-reports-on-behalf',
    label: 'Send scheduled reports on behalf of any rep',
    surface: 'BFF /api/reports admin override',
    group: 'Sales',
    roles: ['Sales.Admin'],
  },
  {
    id: 'sales.run-large-case-alert',
    label: 'Run the large-case alert check on demand',
    surface:
      'Sales Admin page, Large Case Alert section (Run check now / preview); BFF /api/large-case-alert/run. Editing the threshold and recipients rides the normal admin-config write, this gates firing the emails outside the daily schedule.',
    group: 'Sales',
    roles: ['Sales.Admin'],
  },

  // ── Marketing ──
  {
    id: 'marketing.view-sync',
    label: 'View the Braze sync dashboard',
    surface: 'Marketing module; BFF /api/marketing read routes',
    group: 'Marketing',
    anyRoleWithPrefix: 'Marketing.',
  },
  {
    id: 'marketing.configure-sync',
    label: 'Configure and trigger Braze sync',
    surface: 'BFF /api/marketing admin routes',
    group: 'Marketing',
    roles: ['Marketing.Admin'],
  },

  // ── Ideas ──
  {
    id: 'ideas.admin',
    label: 'Ideas tech-team controls (status, planned date, merge)',
    surface: 'Ideas board admin affordances; BFF ideas admin routes (also honors the ideas.idea_admin Postgres table)',
    group: 'Ideas',
    roles: ['Ideas.Admin'],
  },

  // ── Finance ──
  // The two admin-fee gates (Sasha, 9/04). Edit is satisfied by ANY Finance
  // role (parity with the old requireFinanceAccess gate on these routes) OR
  // the dedicated AdminFee.Editor, which exists so a breakout author (AE, imp
  // manager, sales ops) needs no Finance-module grant. Approve is satisfied
  // by AdminFee.Approver ALONE: separation of duty is the point, so neither
  // Finance.Admin/Manager nor any Ops role implies it.
  {
    id: 'adminfee.edit',
    label: 'Author the Rates & Fees plan-year surface and submit tabs for approval',
    surface:
      'Ops group Rates & Fees tab write actions (breakout components/tabs, rate sets, captive terms, budget census, Pull from Catalog, Save version, Submit for approval); BFF /api/finmon/plan-year and /api/finmon/census write routes',
    group: 'Finance',
    roles: [ADMIN_FEE_EDITOR_ROLE],
    anyRoleWithPrefix: 'Finance.',
  },
  {
    id: 'adminfee.approve',
    label: 'Approve or return submitted breakout tabs (the approval queue)',
    surface:
      '/ops/approvals page and its nav badge; BFF /api/finmon/approvals routes and the approve/return/unapprove transitions on a breakout tab. Every decision lands in the review-event log.',
    group: 'Finance',
    roles: [ADMIN_FEE_APPROVER_ROLE],
  },
  {
    id: 'finance.commissions',
    label: 'View and run commission economics (recipients, rates, rep pay)',
    surface:
      'Fee registry commitment rows + commission layer (masked by default) and commission runs; BFF /api/finmon/registry commission routes. Every reveal and run is audited.',
    group: 'Finance',
    roles: [FINANCE_COMMISSIONS_ROLE],
  },

  // ── Implementation ──
  {
    id: 'impform.manage',
    label: 'Manage implementation forms (invite, review, release)',
    surface: 'Imp Forms UI; BFF /api/impform manager routes',
    group: 'Implementation',
    roles: [IMPFORM_MANAGER_ROLE],
  },

  // ── Academy ──
  // Deliberate split (PLAN 12.2): academy.assign is Admin-only while
  // academy.author is not. Writing a lesson is safe; assigning mandatory
  // training with due dates to 150 people and emailing their managers is
  // not, and should not ride on the same grant as fixing a typo.
  {
    id: 'academy.author',
    label: 'Create and publish Academy content',
    surface: 'Academy authoring pages (/academy/authoring); BFF /api/academy author routes (items, blocks, publish, collections, terms, assets)',
    group: 'Academy',
    roles: ['Academy.Author', 'Academy.Admin'],
  },
  {
    id: 'academy.manage-courses',
    label: 'Build courses, paths, and assessments',
    surface: 'Academy course builder (Phase 2); BFF /api/academy course routes',
    group: 'Academy',
    roles: ['Academy.Author', 'Academy.Admin'],
  },
  {
    id: 'academy.assign',
    label: 'Create and activate assignment rules',
    surface: 'Academy assignment rules (Phase 4); BFF /api/academy assignment routes',
    group: 'Academy',
    roles: ['Academy.Admin'],
  },
  {
    id: 'academy.view-team-progress',
    label: 'See completion for your reporting chain',
    surface: 'Academy My Team dashboard (Phase 5); BFF /api/academy team-progress routes',
    group: 'Academy',
    roles: ['Academy.Manager', 'Academy.Admin'],
  },
  {
    id: 'academy.view-all-progress',
    label: 'Org-wide completion and score reporting',
    surface: 'Academy Leadership/HR dashboard (Phase 5); BFF /api/academy reporting routes',
    group: 'Academy',
    roles: ['Academy.Admin'],
  },
  {
    id: 'academy.waive',
    label: 'Waive or revoke an assignment',
    surface: 'Academy assignment detail (Phase 4); BFF /api/academy waive/revoke routes',
    group: 'Academy',
    roles: ['Academy.Admin'],
  },
  {
    // Sits with waive/revoke rather than with reporting: a reset erases a
    // training record (and can revoke a certificate), which is an
    // administrative act, not a reporting one. Managers see completion; they
    // do not get to take it back.
    id: 'academy.reset-quiz',
    label: "Reset a person's course quiz",
    surface: 'Academy Training Report row action; BFF /api/academy/admin/quiz-reset routes',
    group: 'Academy',
    roles: ['Academy.Admin'],
  },

  // ── Portfolio ──
  // Module access rides the Portfolio.* prefix (hasModuleAccess), not a
  // capability, per the registry rule. Owner edits stay a data-level check
  // (accountable person or delivery lead on the initiative row). The two
  // capabilities are the named gates: seeing sensitive rows, and the
  // administrative acts that change WHO is accountable or WHAT exists.
  {
    id: 'portfolio.view-sensitive',
    label: 'See initiatives marked sensitive',
    surface:
      'Portfolio list / detail / dashboard rows where the initiative is flagged sensitive; BFF /api/portfolio read filtering. An initiative’s own accountable person and delivery lead always see it regardless of this capability.',
    group: 'Portfolio',
    roles: ['Portfolio.Sensitive', 'Portfolio.Admin'],
  },
  {
    id: 'portfolio.update-any',
    label: 'Submit monthly updates and manage blockers on any initiative',
    surface:
      'Portfolio My Updates (full worklist) and the snapshot/blocker endpoints on initiatives the user is not listed on. Delegation for the update ritual; no register edit rights. Sensitive rows still require portfolio.view-sensitive.',
    group: 'Portfolio',
    roles: ['Portfolio.Updater', 'Portfolio.Admin'],
  },
  {
    id: 'portfolio.admin',
    label: 'Administer the Initiative Portfolio (create, archive, reassign, mark sensitive, backfill)',
    surface:
      'Portfolio new-initiative and edit dialogs (ownership + archive + sensitive fields); BFF /api/portfolio admin routes, ownership/archive/sensitive PATCH fields, and past-period snapshot backfill',
    group: 'Portfolio',
    roles: ['Portfolio.Admin'],
  },
  {
    // The whole VELT layer is one gate: the weekly meeting agendas, the notes
    // taken on the call, leadership action items, and the is_velt flag. It is
    // NOT a visibility tier over initiatives (is_sensitive does that job);
    // VELT initiatives read normally for every Portfolio.User, only the
    // leadership CONTENT about them is gated.
    //
    // excludesGlobalAdmin (Sasha, 2026-08-27): agenda notes can be sensitive
    // about people, so the admin RANK does not open the meeting room. Admins
    // keep the whole register, VELT-flagged initiatives included; sitting in
    // the leadership call takes a Portfolio.Leadership grant of one's own.
    id: 'portfolio.velt',
    label: 'Use the VELT leadership review (agendas, meeting notes, leadership action items)',
    surface:
      'Portfolio VELT pages (/portfolio/velt and its meetings), the Leadership card on initiative detail, the VELT flag on an initiative, and every BFF /api/portfolio/velt route. Sensitive initiatives are still filtered per viewer: a VELT member without portfolio.view-sensitive cannot agenda or read them. Global Admin does NOT qualify by rank.',
    group: 'Portfolio',
    roles: ['Portfolio.Leadership'],
    excludesGlobalAdmin: true,
  },

  // ── Share ──
  // Module access (Share.User, the gate on seeing the module at all) rides the
  // Share.* prefix via hasModuleAccess, not a capability, per the registry
  // rule. Which FILES a person can open is a data-level check: a row in
  // share.file_recipient naming them. These two capabilities are the named
  // gates on top of that.
  {
    id: 'share.publish',
    label: 'Publish files to Vera Share and choose who receives them',
    surface:
      'Vera Share publish pages; BFF /api/share/published routes. The privileged act in the module: a publisher decides who can see content that may contain PHI. Publishers can manage only files they themselves published.',
    group: 'Share',
    roles: ['Share.Publisher', 'Share.Admin'],
  },
  {
    id: 'share.admin',
    label: 'Administer every Vera Share file and recipient list',
    surface:
      'Vera Share manage list (all publishers) and every /api/share/published route on a file the caller did not publish. Also the only way to open a file nobody named you on, which is why it is a separate grant from publishing.',
    group: 'Share',
    roles: ['Share.Admin'],
  },

  // ── Rx ──
  // Module access (Rx.User) rides the Rx.* prefix via hasModuleAccess, per the
  // registry rule. These are the named gates on the three privileged acts:
  // spending Fabric capacity, changing what the savings model believes, and
  // putting a prospect's claims into the app.
  {
    id: 'rx.evidence_refresh',
    label: 'Refresh the book-of-business evidence from Fabric',
    surface:
      'Vera Rx evidence page; BFF POST /api/rx/evidence/refresh. Runs the gold-lakehouse queries and rewrites the recent evidence months, which moves every savings percentage the module shows. The weekly scheduler calls the same route with its own key. Rx.Refresh grants this and nothing else, so keeping the data current does not require the rest of Rx.Admin.',
    group: 'Rx',
    roles: ['Rx.Admin', 'Rx.Refresh'],
  },
  {
    id: 'rx.expectations',
    label: 'Edit the PIP and specialty savings expectations',
    surface:
      'Vera Rx expectations page; BFF write routes under /api/rx/expectations. These rows decide what the model claims where no price list exists, so an edit changes future proposal numbers. History is effective-dated and never overwritten.',
    group: 'Rx',
    roles: ['Rx.Admin'],
  },
  {
    id: 'rx.prospect_claims',
    label: 'Upload a prospect claim file and run a savings analysis',
    surface:
      'Vera Rx runs page; BFF POST /api/rx/prospects/:id/files. The uploaded file contains PHI: it is parsed in the request and never stored, member identifiers are hashed at ingest, and the resulting run is visible to Rx.User readers.',
    group: 'Rx',
    roles: ['Rx.Admin'],
  },
  {
    id: 'rx.reference_lists',
    label: 'Manage the Rx reference lists (Cost Plus prices, PIP drugs, copay cards)',
    surface:
      'Vera Rx reference lists page; BFF write routes under /api/rx/references. These lists are what the savings engine prices and routes against, so importing a drop or editing a row changes future proposal numbers. Imports preview before they apply, rows are effective-dated rather than overwritten, and Rx.User can read every list without this grant.',
    group: 'Rx',
    roles: ['Rx.Admin'],
  },

  // ── Maple ──
  // Module access (Maple.User) rides the Maple.* prefix via hasModuleAccess.
  // This is the one named gate: every other Maple write stamps or annotates
  // claims that already exist, while the 835 load creates them.
  {
    id: 'maple.load-835',
    label: 'Take a Procare 835 into Maple',
    surface:
      'Cloud / Procare hub and the batch reconcile page (Load 835); BFF POST /api/maple/procare/load. Creates maple_claim rows from the remittance file and the Bronze claim detail, which is how pharmacy money enters the module. Previewing a load, and seeing that a remittance is outstanding, need only Maple access.',
    group: 'Maple',
    roles: ['Maple.Admin', 'Maple.Manager'],
  },
] as const;

const CAPABILITY_BY_ID = new Map(CAPABILITIES.map((c) => [c.id, c]));

/**
 * The single capability check. Global Admin always qualifies. Unknown ids
 * return false (fail closed) rather than throwing so a stale generated copy
 * in the BFF can never turn into an open gate.
 */
export function can(capabilityId: string, roles: readonly string[]): boolean {
  const cap = CAPABILITY_BY_ID.get(capabilityId);
  if (!cap) return false;
  if (hasGlobalAdmin(roles) && !cap.excludesGlobalAdmin) return true;
  if (cap.roles?.some((r) => roles.includes(r))) return true;
  if (cap.anyRoleWithPrefix && roles.some((r) => r.startsWith(cap.anyRoleWithPrefix as string))) return true;
  return false;
}

/**
 * Every capability a single role value unlocks. Drives the Admin page's
 * "what this role unlocks" listing. Global Admin unlocks everything except
 * capabilities marked excludesGlobalAdmin (today: the VELT meeting layer).
 */
export function capabilitiesForRole(roleValue: string): VeraCapability[] {
  return CAPABILITIES.filter((cap) => can(cap.id, [roleValue]));
}

// ---------------------------------------------------------------------------
// Named wrappers. These predate the registry; they stay so existing call
// sites keep working, but each is now a one-line view over a capability.
// ---------------------------------------------------------------------------

/** Can manage public demo pages (Admin > Demo Pages tab + /api/demo-pages). */
export function canManageDemoPages(roles: readonly string[]): boolean {
  return can('platform.demo-pages', roles);
}

/**
 * Can publish the header broadcast banner (Admin > Broadcasts + the
 * /api/shell/broadcasts write routes). Reading a broadcast needs nothing but a
 * session; this gates writing one, which reaches a whole audience with no
 * per-message review.
 */
export function canBroadcast(roles: readonly string[]): boolean {
  return can('platform.broadcast', roles);
}

/**
 * Can manage implementation forms (/api/impform/*): create instances, invite
 * recipients, run review, release. External fillers never reach this gate;
 * they authenticate via recipient sessions, not Entra.
 */
export function canManageImpForms(roles: readonly string[]): boolean {
  return can('impform.manage', roles);
}

/**
 * Can use the Ops CRM Matches / account-link repair tools. Deliberately NOT
 * granted to every Ops.Admin: this surface does bulk Dataverse re-parenting,
 * so it is gated to global Admin or the dedicated Ops.DataAdmin role.
 */
export function canManageCrmMatches(roles: readonly string[]): boolean {
  return can('ops.crm-matches', roles);
}

/**
 * Can run the destructive delete actions inside a group: delete all solutions
 * on a plan year, delete a plan year, and nuke GI data. Ops.Admin and global
 * Admin are supersets. Deleting an ENTIRE group is covered by the separate
 * ops.delete-group capability (admin only).
 */
export function canOpsDelete(roles: readonly string[]): boolean {
  return can('ops.delete-plan-year', roles);
}

/**
 * Can deactivate / reactivate a group from the groups list. Deactivating is the
 * soft form of taking a group off the book (it stays queryable but drops out of
 * the active views), so it rides the same admin-only gate as deleting one.
 * (A8, 7/23 review: this used to be an ungated, no-confirm menu item.)
 */
export function canDeactivateGroup(roles: readonly string[]): boolean {
  return can('ops.delete-group', roles);
}

/**
 * Can set or clear a group's term date, the CRM account's dts_termdate. The
 * dated FACT the derived lifecycle (active / terming / termed) is computed
 * from everywhere (docs/design/group-lifecycle/TERMED-GROUPS.md), so it is
 * granted to the same roles as canDeactivateGroup: recording that a group
 * left is at least as consequential as shelving one.
 */
export function canTermGroup(roles: readonly string[]): boolean {
  return can('ops.term-group', roles);
}

/**
 * Can fire the book-wide Catalog runs: propagate pending instances across every
 * active group, sweep retired data, or apply a bulk update. Read access to the
 * Propagation page is unrestricted (the plan is useful reference); this gates
 * the buttons that write. (D1, 7/23 review: previously ungated.)
 */
export function canRunPropagation(roles: readonly string[]): boolean {
  return can('catalog.run-propagation', roles);
}

/**
 * Can take a Procare 835 into Maple (the in-app load, Procare step 4). Gated
 * above bare Maple access because it CREATES claim rows: every other write in
 * the module stamps or annotates claims that already exist. Previewing a load
 * is deliberately not gated here, so anyone with Maple access can see that a
 * remittance is sitting outstanding.
 */
export function canLoadRemittance(roles: readonly string[]): boolean {
  return can('maple.load-835', roles);
}
